import { ApiAudience, ApiConditionGroup, ApiProject } from "@incendium/api";
import { useConditionGroupContext } from "Components/ConditionalGroup/ConditionGroupProvider";
import ConditionGroupsFormWrapper from "Components/ConditionalGroup/ConditionGroupsFormWrapper";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import AudienceRules from "./AudienceRules";
import AudienceSidebar from "./AudienceSidebar";
import { saveAudience } from "features/audiences/services/audienceService";
import { useNotification } from "Hooks";
import { produce } from "immer";
import { useAudiences } from "Hooks/useAudiences";

interface IAudienceFormProps {
  audience: ApiAudience;
  setAudience: React.Dispatch<React.SetStateAction<ApiAudience>>;
  project: ApiProject;
  isNew?: boolean;
}

function AudienceForm({ audience, setAudience, project, isNew }: IAudienceFormProps) {
  const history = useHistory();
  const { conditionGroups, setConditionGroups } = useConditionGroupContext();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { audiences, setAudiences } = useAudiences();
  useEffect(() => {
    if (!audience || conditionGroups.length) {
      return;
    }

    const groups = audience.conditionGroups || [];
    setConditionGroups(
      groups && groups.length > 0
        ? [...groups]
        : [
          {
            conditions: [{}],
          },
        ]
    );
  }, [audience, setConditionGroups, conditionGroups]);

  const onSubmit = async () => {
    try {
      const res = await saveAudience(project.id as number, {
        ...audience,
        conditionGroups: conditionGroups as ApiConditionGroup[],
      });

      setAudiences(produce(audiences, (draft) => {
        if (isNew) {
          draft.push(res);
          return;
        }

        const idx = draft.findIndex((a) => a.id === res.id);
        if (idx < 0) {
          return;
        }
        draft[idx] = res;
      }));

      if (isNew && history.location.pathname.includes("new")) {
        history.push(`/clients/${project.clientId}/projects/${project?.id}/audience/audiences/${res.id}`);
      }

      showSuccessNotification(`${res.name} Updated`);
    } catch (error) {
      showErrorNotification("Something Went Wrong");
    }
  };

  const onCancel = () => {
    history.push(`../audiences`);
  };

  return (
    <ConditionGroupsFormWrapper
      onSubmit={onSubmit}
      onCancel={onCancel}
      rules={<AudienceRules />}
    >
      <AudienceSidebar audience={audience} setAudience={setAudience} />
    </ConditionGroupsFormWrapper>
  );
}

export default AudienceForm;
